import React, { useEffect, useMemo, useRef, useState } from 'react';
import { OmniIconComponent, OmniTooltipNextComponent, Utils } from '../../../../../shared';
import { AgGridReact } from 'ag-grid-react';
import { PlacementUtils } from '../../../../placements';

/**
 * MediaOrdersTable Component (Skeleton)
 * 
 * This component will display a hierarchical table of media orders using AG Grid
 * 
 * Props:
 * @param {String} campaignId - Campaign ID to fetch media orders for
 * @param {Object} filters - Filter options to apply on the table
 * 
 * TODO for teammate:
 * - Implement AG Grid with tree data structure
 * - Columns needed:
 *   - Expand/collapse icon (for hierarchical data)
 *   - Description (with folder icon for parent rows)
 *   - Flight (date range)
 *   - ID
 *   - Planned (currency format)
 *   - Ordered (currency format)
 *   - Executed (currency format)
 *   - Actions menu (3-dot menu)
 * 
 * - Features to implement:
 *   - Expandable/collapsible rows for nested data
 *   - Parent rows showing aggregated data
 *   - Child rows showing individual placements (Pandora, DV360, CM360, etc.)
 *   - Currency formatting for monetary values
 *   - Action menu with options
 *   - Responsive column sizing
 * 
 * - Data structure example from screenshot:
 *   Parent: "Essentials SANDBOX_Another Campaign..." with children:
 *     - Pandora (Dec 1, 2024 - Mar 1, 2024) - $80,000.00 planned
 *     - DV360 (Dec 1, 2024 - Mar 1, 2024) - $80,000.00 planned
 *     - CM360 (Dec 1, 2024 - Mar 1, 2024) - $20,000.00 planned, $20,000.00 ordered (Ad Serving Fee)
 * 
 * Note: Filter and plus icons are in parent component (campaignDetailComponentV3.jsx)
 */
const MediaOrdersTable = (params) => {

    // todo: need to updated the publisher_budget_in_pmds key after api integration
    const plannedRenderer = useMemo(() => { // wip
        return (props) => {
            const isLocal = params?.systemParametersData?.data?.isLocal;
            const currency = isLocal ? props?.data?.local_currency_name : 'USD' || 'USD';

            if (props?.data?.dataTypeForAggrid === 'campaign') {
                const amount = isLocal ? props?.data?.campaignPlannedTotalLocal : props?.data?.campaignPlannedTotal;
                const displayValue = amount != null
                    ? Utils.getLocalCurrency(currency, amount)
                    : '-';
                return <span>{displayValue}</span>;

            } else if (props?.data?.dataTypeForAggrid === 'channel') {
                const amount = isLocal ? props?.data?.channel_total_budget_in_pmds_local : props?.data?.channel_total_budget_in_pmds;
                const displayValue = amount != null
                    ? Utils.getLocalCurrency(currency, amount)
                    : '-';
                return <span>{displayValue}</span>;

            } else if (props?.data?.dataTypeForAggrid === 'publisher') {
                const amount = isLocal ? props?.data?.publisher_budget_in_pmds_local : props?.data?.publisher_budget_in_pmds;
                const displayValue = amount != null
                    ? Utils.getLocalCurrency(currency, amount)
                    : '-';

                if (props?.data?.publisher_type === 'Ad Serving') {
                    return (
                        <span className="ad-serving-fee-pill-container">
                            <span className="ad-serving-fee-pill">Ad Serving Fee</span>
                            {displayValue}
                        </span>
                    );
                } else {
                    return <span>{displayValue}</span>;
                }

            }
        };
    }, [params?.systemParametersData?.data?.isLocal]); // Re-create only when isLocal changes

    const orderedRenderer = useMemo(() => {
        return (props) => {
            if (props?.data?.dataTypeForAggrid === 'campaign') {
                const isLocal = params?.systemParametersData?.data?.isLocal;
                const currency = isLocal ? props?.data?.local_currency_name : 'USD' || 'USD';
                const amount = isLocal ? props?.data?.campaignOrderedTotalLocal : props?.data?.campaignOrderedTotal;
                const displayValue = amount != null
                    ? Utils.getLocalCurrency(currency, amount)
                    : '-';
                return <span>{displayValue}</span>;
            } else {
                const isLocal = params?.systemParametersData?.data?.isLocal;
                const currency = isLocal ? props?.data?.local_currency_name : 'USD' || 'USD';
                const amount = isLocal ? props?.data?.amount_sent_local : props?.data?.amount_sent;
                const displayValue = amount != null
                    ? Utils.getLocalCurrency(currency, amount)
                    : '-';
                return <span>{displayValue}</span>;
            }
        };
    }, [params?.systemParametersData?.data?.isLocal]); // Re-create only when isLocal changes

    const applyRenderers = (cols) => {
        const cellRenderers = {
            planned: plannedRenderer,
            ordered: orderedRenderer
        };

        return cols.map(col => {
            // if column has children → recurse
            if (col.children) {
                return {
                    ...col,
                    children: applyRenderers(col.children),
                };
            }

            // otherwise apply renderer if matched
            return cellRenderers[col.field]
                ? { ...col, cellRenderer: cellRenderers[col.field] }
                : col;
        });
    };

    const [campaignTaxonomy, setCampaignTaxonomy] = useState(null);
    const [taxonomyName, setTaxonomyName] = useState('');
    const [taxonomyNameWithAbbrevation, setTaxonomyNameWithAbbrevation] = useState('');
    const [taxonomySchema, setTaxonomySchema] = useState('');
    const [data, setData] = useState([]);
    const [columnDefs, setColumnDefs] = useState(applyRenderers(mediaOrdersColumnDefs));
    const [campaignPlannedTotal, setCampaignPlannedTotal] = useState(null);
    const [campaignPlannedTotalLocal, setCampaignPlannedTotalLocal] = useState(null);
    const [campaignOrderedTotal, setCampaignOrderedTotal] = useState(null);
    const [campaignOrderedTotalLocal, setCampaignOrderedTotalLocal] = useState(null);

    const gridRef = useRef(null);
    let gridApi, gridColumnApi;

    // Add gridContext - memoize to prevent unnecessary re-renders
    const gridContext = useMemo(() => ({
        isLocal: params?.systemParametersData?.data?.isLocal,
        campaignObj: params?.campaignObj,
        // Add any other context data needed by cell renderers
    }), [params?.systemParametersData?.data?.isLocal, params?.campaignObj]);

    useEffect(() => {
        updateData();
        // checkCampaignTaxonomy();
    }, [])

    const onGridReady = (params) => {
        gridApi = params.api;
        gridColumnApi = params.columnApi;
    };

    /**
     * Handler for row expand/collapse events
     * @param {Object} event - AG Grid row group opened event
     */
    const onRowGroupOpened = (event) => {
        const rowNode = event.node;
        const isExpanded = rowNode.expanded;
        const rowData = rowNode.data;
        const rowLevel = rowNode.level; // 0 = campaign, 1 = channel, 2 = publisher


        console.log('rowData', rowData);
        

        console.log('Row expand/collapse event:', {
            expanded: isExpanded,
            level: rowLevel,
            dataType: rowData?.dataTypeForAggrid,
            description: rowData?.description,
            rowId: rowNode.id
        });

        // Add custom logic here based on row level or data type
        if (rowLevel === 0) {
            // Campaign level expand/collapse
            console.log(`Campaign ${isExpanded ? 'expanded' : 'collapsed'}:`, rowData?.campaign_taxonomy);
        } else if (rowLevel === 1) {
            // Channel level expand/collapse
            console.log(`Channel ${isExpanded ? 'expanded' : 'collapsed'}:`, rowData?.channel_name);
        }

        // You can add additional actions here, such as:
        // - Lazy loading nested data
        // - Analytics tracking
        // - State management updates
    };

    const updateData = () => {
        const campaignDataWithChildren = params?.campaignDataWithChildren || [];

        if (!campaignDataWithChildren.length) {
            setData([]);
            return;
        }

        const data = [];
        const channelsLength = campaignDataWithChildren.length;

        for (let campaignIndex = 0; campaignIndex < channelsLength; campaignIndex++) {
            const campaign = campaignDataWithChildren[campaignIndex];
            const channels = campaign?.channels || [];

            // Initialize totals at campaign level
            let campaignPlannedTotal = 0;
            let campaignOrderedTotal = 0;
            let campaignPlannedTotalLocal = 0;
            let campaignOrderedTotalLocal = 0;

            // Set campaign-level properties once
            campaign.nestedData = channels;
            campaign.description = campaign.parent_id ? campaign.campaign_taxonomy : campaign.campaign_display_name;
            campaign.tier_id_display = '';
            campaign.dataTypeForAggrid = 'campaign';

            // Process channels
            const channelsCount = channels.length;
            for (let channelIndex = 0; channelIndex < channelsCount; channelIndex++) {
                const channel = channels[channelIndex];
                const publishers = channel?.publishers || [];
                const localCurrency = campaign?.local_currency_name;

                // Set channel properties
                channel.description = channel.channel_name;
                channel.flight = `${Utils.formatDateObject(channel.channel_startdate)} - ${Utils.formatDateObject(channel.channel_enddate)}`;
                channel.tier_id_display = channel.channel_tier_id || '-';
                channel.dataTypeForAggrid = 'channel';
                channel.local_currency_name = localCurrency;

                // Accumulate totals (use || 0 to prevent NaN)
                campaignPlannedTotal += channel?.channel_total_budget_in_pmds || 0;
                campaignOrderedTotal += channel?.amount_sent || 0;
                campaignPlannedTotalLocal += channel?.channel_total_budget_in_pmds_local || 0;
                campaignOrderedTotalLocal += channel?.amount_sent_local || 0;

                // Process publishers if present
                const publishersCount = publishers.length;
                if (publishersCount > 0) {
                    channel.nestedData = publishers;

                    for (let publisherIndex = 0; publisherIndex < publishersCount; publisherIndex++) {
                        const publisher = publishers[publisherIndex];

                        publisher.description = publisher.publisher_name;
                        publisher.flight = `${Utils.formatDateObject(publisher.publisher_start_date)} - ${Utils.formatDateObject(publisher.publisher_end_date)}`;
                        publisher.tier_id_display = publisher.tier_id || '-';
                        publisher.dataTypeForAggrid = 'publisher';
                        publisher.local_currency_name = localCurrency;
                    }
                }
            }

            // Set campaign totals after processing all channels
            campaign.campaignPlannedTotal = campaignPlannedTotal;
            campaign.campaignOrderedTotal = campaignOrderedTotal;
            campaign.campaignPlannedTotalLocal = campaignPlannedTotalLocal;
            campaign.campaignOrderedTotalLocal = campaignOrderedTotalLocal;

            data.push(campaign);
        }

        setData(data);
    };

    const checkCampaignTaxonomy = () => {
        if (params?.campaignObj?.campaign_taxonomy?.split(':|')[0] === "-" || !params.campaignObj?.campaign_taxonomy?.split(':|')[0]) {
            generateCampaignTaxonomy();
        } else {
            setCampaignTaxonomy(params.campaignObj?.campaign_taxonomy);
        }
    }

    const generateCampaignTaxonomy = () => {
        // Implementation for generating campaign taxonomy
        let campaignTaxonomyName = "", camapaignTaxonomyNameWithAbbrevation = "", taxonomySchema = "";
        let campaignObj = params?.campaignObj;
        let taxonomyJson = [];

        params.campaignTaxonomyData && params.campaignTaxonomyData.fields.forEach((field, index) => {
            let taxonomyField = {
                code: "NU",
                delimiter: field['delimiter_value'],
                id: field['id'] || null
            }
            if (field?.name?.toLowerCase() === 'channel') {
                if (campaignObj['channels'].length > 1) {
                    campaignTaxonomyName += 'MUL' + field['delimiter_value'];
                    camapaignTaxonomyNameWithAbbrevation += 'MUL' + field['delimiter_value'];
                    taxonomyField.code = 'MUL';
                } else if (campaignObj['channels'].length === 1) {
                    campaignTaxonomyName += campaignObj['channels'][0]['channel_name'] || "NU";
                    campaignTaxonomyName += field['delimiter_value'];
                    camapaignTaxonomyNameWithAbbrevation += campaignObj['channels'][0]['channel_name'] || " ";
                    camapaignTaxonomyNameWithAbbrevation += field['delimiter_value'];
                    taxonomyField.code = campaignObj['channels'][0]['channel_name'] || "NU";
                } else {
                    campaignTaxonomyName += "NU" + field['delimiter_value'];
                    camapaignTaxonomyNameWithAbbrevation += " " + field['delimiter_value'];
                }
                taxonomySchema += "Channel" + '|';
            } else {
                /* field_type =1 for Dropdown field
                  field_type=2 for FreeText field
                  field_type= 9 for Static Text
                */

                switch (field.field_type) {
                    case 1:
                        let fieldCodeValue, fieldName = "";
                        fieldCodeValue = field.fielditems.filter(fielditem => fielditem['name'] === campaignObj[field?.name?.toLowerCase()]).map((fielditem) => {
                            fieldName = fielditem.name || "";
                            return fielditem.code;
                        })
                        campaignTaxonomyName += (fieldCodeValue[0] || "NU") + field['delimiter_value']
                        camapaignTaxonomyNameWithAbbrevation += fieldName + field['delimiter_value']
                        taxonomyField.code = fieldCodeValue[0] || 'NU';
                        break;
                    case 2:
                        campaignTaxonomyName += (campaignObj[field?.name?.toLowerCase()] || "NU") + field['delimiter_value']
                        camapaignTaxonomyNameWithAbbrevation += campaignObj[field?.name?.toLowerCase()] + field['delimiter_value']
                        taxonomyField.code = campaignObj[field?.name?.toLowerCase()] || "NU";
                        break;
                    case 9:
                        campaignTaxonomyName += field.static_field_value + field['delimiter_value']
                        camapaignTaxonomyNameWithAbbrevation += field.static_field_value + field['delimiter_value']
                        taxonomyField.code = field.static_field_value;
                        break;
                    case 10:
                        campaignTaxonomyName += (campaignObj[field?.name?.toLowerCase()] || "{TaxonomyValueID}") + field['delimiter_value']
                        camapaignTaxonomyNameWithAbbrevation += campaignObj[field?.name?.toLowerCase()] + field['delimiter_value']
                        taxonomyField.code = campaignObj[field?.name?.toLowerCase()] || "{TaxonomyValueID}";
                        break;
                    default:
                        campaignTaxonomyName += (campaignObj[field?.name?.toLowerCase()] || "NU") + field['delimiter_value']
                        camapaignTaxonomyNameWithAbbrevation += campaignObj[field?.name?.toLowerCase()] + field['delimiter_value']
                        taxonomyField.code = campaignObj[field?.name?.toLowerCase()] || "NU";
                        break;
                }
                taxonomySchema += field["name"] + '|';
            }

            taxonomyJson.push(taxonomyField);
            if (index === params.campaignTaxonomyData.fields.length - 1) {
                setTaxonomyName(
                    campaignObj['synthetic_id']
                        ? campaignTaxonomyName.slice(0, -1) + ":|" + campaignObj['synthetic_id']
                        : campaignTaxonomyName.slice(0, -1)
                );
                setTaxonomyNameWithAbbrevation(camapaignTaxonomyNameWithAbbrevation.slice(0, -1));
                setTaxonomySchema(taxonomySchema.slice(0, -1));
            }
        });
    }

    const openPlacements = () => {
        // params.navigate(params?.campaignObj, 'placementDetailsTable');
        Utils.navigateToComponent(params?.campaignObj, 'placementDetailsTable', params)
    };

    const renderIconActions = useMemo(() => {
        return (
            <div className="icons-container">
                <OmniTooltipNextComponent place="top">
                    <OmniIconComponent slot="invoker" className="ordered-icons" icon-id="omni:informative:cart" onclick={() => openPlacements()} />
                    <div slot="content">Open placement</div>
                </OmniTooltipNextComponent>

                <OmniTooltipNextComponent place="top">
                    <OmniIconComponent slot="invoker" className="ordered-icons" icon-id="omni:interactive:more" />
                    <div slot="content">More options</div>
                </OmniTooltipNextComponent>
            </div>
        );
    }, []); // Empty deps - static content

    const renderBudgetInfo = useMemo(() => {
        const isLocal = params?.systemParametersData?.data?.isLocal;
        const currency = isLocal ? params?.campaignObj?.local_currency_name : 'USD';
        const plannedTotal = isLocal ? campaignPlannedTotalLocal : campaignPlannedTotal;
        const orderedTotal = isLocal ? campaignOrderedTotalLocal : campaignOrderedTotal;

        return (
            <div className="budget-info">
                <OmniTooltipNextComponent place="left">
                    <span slot="invoker" className="planned-amount">
                        {plannedTotal ? Utils.getLocalCurrency(currency, plannedTotal) : "-"}
                    </span>
                    <div slot="content">Planned amount</div>
                </OmniTooltipNextComponent>

                <OmniTooltipNextComponent place="left">
                    <span slot="invoker" className="ordered-amount">
                        / {orderedTotal ? Utils.getLocalCurrency(currency, orderedTotal) : "-"}
                    </span>
                    <div slot="content">Ordered amount</div>
                </OmniTooltipNextComponent>
            </div>
        );
    }, [
        params?.systemParametersData?.data?.isLocal,
        params?.campaignObj?.local_currency_name,
        campaignPlannedTotal,
        campaignPlannedTotalLocal,
        campaignOrderedTotal,
        campaignOrderedTotalLocal
    ]);

    return (
        <div className="media-orders-table">

            <div className="header-section">
                <div className="campaign-title-section">
                    <span className="campaign-title">{params?.campaignObj?.campaign_taxonomy || taxonomyName}</span>
                    <span className="prisma-id">Prisma ID: {params?.campaignObj?.synthetic_id}</span>
                </div>

                <div className="icon-section">
                    {/* {renderIconActions}
                    {renderBudgetInfo} */}
                </div>
            </div>

            {data.length > 0 && <AgGridReact
                className="media-orders-aggrid-table"
                key={`grid-${data.length}-${Date.now()}`}
                ref={gridRef}
                columnDefs={columnDefs}
                rowData={data}
                treeData={true}
                treeDataChildrenField={"nestedData"}
                groupDefaultExpanded={0}
                suppressContextMenu={true}
                autoGroupColumnDef={{
                    headerName: "Description",
                    field: "description",
                    cellRenderer: 'agGroupCellRenderer',
                    cellRendererParams: {
                        suppressCount: true,
                        innerRenderer: (params) => {

                            // Handle completely missing data
                            if (!params.data) {
                                console.error('innerRenderer: No data - this is likely the phantom row');
                                return ''; // Return empty string to hide the phantom row
                            }

                            // Handle valid data - return plain text only
                            const description = params.data.description || params.value || '';
                            const isChannel = !params.data.publisher_name;

                            if (isChannel) {
                                return description;
                            } else {
                                return `  ${description}`; // Just add spaces for indentation instead of HTML
                            }
                        }
                    },
                    minWidth: 550,
                    flex: 2
                }}
                getRowId={(params) => {
                    const rowId = `${params?.data?.id}-${params?.data?.publisher_name ? 'pub' : 'chan'}-${PlacementUtils.generateRandomId()}`;
                    return rowId;
                }}
                defaultColDef={defaultColDef}
                sideBar={false}
                context={gridContext}
                domLayout="autoHeight"
                onGridReady={onGridReady}
                onRowGroupOpened={onRowGroupOpened}
            />}

        </div>
    );
};

export default MediaOrdersTable;

export const defaultColDef = {
    flex: 5,
    filter: false,
    resizable: true,
    wrapText: true,
    autoHeight: true,
    minWidth: 150,
    floatingFilter: false,
    suppressHeaderMenuButton: true,
};

export const mediaOrdersColumnDefs = [
    {
        headerName: "Flight",
        field: "flight",
        resizable: true,
        minWidth: 140,
        wrapText: true,
        autoHeight: true,
        flex: 1,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
    }, {
        headerName: "ID",
        field: "tier_id_display",
        resizable: true,
        minWidth: 70,
        wrapText: true,
        autoHeight: true,
        flex: .5,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
    }, {
        headerName: "Ordered",
        field: "ordered",
        resizable: true,
        minWidth: 120,
        wrapText: true,
        autoHeight: true,
        flex: 1,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
        cellStyle: { textAlign: 'right' }
    },
    // need to implement "Executed" and "Planned" column in future
    // {
    //     headerName: "Planned",
    //     field: "planned",
    //     resizable: true,
    //     minWidth: 250,
    //     wrapText: true,
    //     autoHeight: true,
    //     flex: 1,
    //     filter: false,
    //     floatingFilter: false,
    //     suppressFloatingFilterButton: false,
    //     cellStyle: { textAlign: 'right' }
    // }, 
    // {
    //     headerName: "Executed",
    //     field: "executed",
    //     resizable: true,
    //     minWidth: 120,
    //     wrapText: true,
    //     autoHeight: true,
    //     flex: 1,
    //     filter: false,
    //     floatingFilter: false,
    //     suppressFloatingFilterButton: false,
    //     cellStyle: { textAlign: 'right' }
    // }
];
